import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { sparksAPI } from '../lib/api';
import { getViewedSet, isAuthorFullyViewed } from '../lib/sparksViewed';
import { useTheme } from '../context/ThemeContext';
import { cn } from '../lib/utils';
import { Plus, Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';
import SparksRing from './SparksRing';
import SparksViewer from './SparksViewer';
import CreateSparkDialog from './CreateSparkDialog';

/**
 * SparksTray — Instagram-style horizontal carousel of authors with active sparks.
 *
 * Props:
 *   currentUser — optional logged-in user object (shows the "+ Add Spark" button)
 *   onChange    — callback when sparks change (e.g. after creation)
 */
export default function SparksTray({ currentUser, className = '', onChange }) {
  const { isDarkish: isDark } = useTheme();
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openIndex, setOpenIndex] = useState(-1);
  const [createOpen, setCreateOpen] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const trayRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await sparksAPI.getFeed(50);
      const myUid = currentUser ? (currentUser.user_id || currentUser.id) : null;
      const all = r.data?.authors || [];
      const seen = getViewedSet();
      const visible = all.filter(a => {
        if (myUid && a.user_id === myUid) return true;
        if (!a.has_unviewed) return false;
        if (isAuthorFullyViewed(a, seen)) return false;
        return true;
      });
      setAuthors(visible);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => { load(); }, [load]);

  // Track scroll edges to show chevron affordances
  const updateEdges = useCallback(() => {
    const el = trayRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = trayRef.current;
    if (!el) return;
    updateEdges();
    el.addEventListener('scroll', updateEdges, { passive: true });
    window.addEventListener('resize', updateEdges);
    return () => {
      el.removeEventListener('scroll', updateEdges);
      window.removeEventListener('resize', updateEdges);
    };
  }, [authors, loading, updateEdges]);

  const scrollBy = (dx) => {
    trayRef.current?.scrollBy({ left: dx, behavior: 'smooth' });
  };

  const handleCreated = async () => {
    setCreateOpen(false);
    await load();
    onChange?.();
  };

  const openAuthor = (idx) => setOpenIndex(idx);
  const closeViewer = () => { setOpenIndex(-1); load(); };

  const myEntry = currentUser
    ? authors.find(a => a.user_id === (currentUser.user_id || currentUser.id))
    : null;

  // Section header: count only (no eyebrow label)
  const Eyebrow = () => null;

  // Skeleton loading placeholders
  if (loading) {
    return (
      <div className={cn('relative flex items-center gap-4 py-1', className)} data-testid="sparks-tray-loading">
        <Eyebrow />
        <div className="flex items-center gap-3 overflow-hidden flex-1">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="flex flex-col items-center gap-1.5 shrink-0">
              <div
                className={cn(
                  'w-14 h-14 rounded-full animate-pulse',
                  isDark ? 'bg-white/[0.04]' : 'bg-gray-200'
                )}
              />
              <div className={cn('w-10 h-2 rounded animate-pulse', isDark ? 'bg-white/[0.04]' : 'bg-gray-200')} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const showSelfButton = !!currentUser;
  // Always render the tray shell so anonymous users still see the "Vibes" label
  // and a call-to-action, matching the polished home hero.

  return (
    <div className={cn('relative', className)} data-testid="sparks-tray">
      <div className="flex items-center gap-4">
        <Eyebrow />

        {/* Rail */}
        <div className="relative flex-1 min-w-0">
          {/* Left fade + chevron */}
          {canScrollLeft && (
            <>
              <div
                aria-hidden
                className={cn(
                  "pointer-events-none absolute inset-y-0 left-0 w-8 z-10",
                  isDark
                    ? "bg-gradient-to-r from-[#0a0a0f] to-transparent"
                    : "bg-gradient-to-r from-gray-50 to-transparent"
                )}
              />
              <button
                type="button"
                aria-label="Scroll left"
                onClick={() => scrollBy(-320)}
                className={cn(
                  "absolute left-0 top-1/2 -translate-y-1/2 z-20 h-7 w-7 rounded-full flex items-center justify-center border transition-all",
                  "opacity-0 group-hover/tray:opacity-100 focus:opacity-100",
                  isDark
                    ? "bg-[#12121a] border-white/10 text-gray-300 hover:bg-[#1a1a24] hover:text-white"
                    : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                  "shadow-[0_4px_10px_-4px_rgba(0,0,0,0.4)]"
                )}
                data-testid="sparks-scroll-left"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>
            </>
          )}

          <div
            ref={trayRef}
            className="group/tray flex items-start gap-3 sm:gap-4 overflow-x-auto py-2 pr-1 scroll-smooth no-scrollbar"
          >
            {/* My Spark — adds new */}
            {showSelfButton && (
              <div className="flex flex-col items-center gap-1.5 shrink-0 group">
                <button
                  type="button"
                  onClick={() => myEntry ? openAuthor(authors.indexOf(myEntry)) : setCreateOpen(true)}
                  data-testid="sparks-add-btn"
                  aria-label={myEntry ? 'Open my Sparks' : 'Add a new Spark'}
                  className="rounded-full outline-none focus-visible:ring-2 focus-visible:ring-violet-500/40 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
                >
                  <div className="relative">
                    {myEntry ? (
                      <SparksRing
                        hasActive
                        viewed={!myEntry.has_unviewed}
                        size={60}
                        thickness={2}
                        testId="sparks-ring-self"
                      >
                        {currentUser.picture ? (
                          <img src={currentUser.picture} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-base font-bold text-white bg-white/[0.08]">
                            {(currentUser.name || '?')[0].toUpperCase()}
                          </div>
                        )}
                      </SparksRing>
                    ) : (
                      <>
                        <div
                          className={cn(
                            "w-[60px] h-[60px] rounded-full overflow-hidden transition-all",
                            "ring-2 ring-dashed",
                            isDark
                              ? "ring-violet-500/30 group-hover:ring-violet-400"
                              : "ring-violet-400/50 group-hover:ring-violet-500"
                          )}
                        >
                          {currentUser.picture ? (
                            <img src={currentUser.picture} alt="" className="w-full h-full object-cover opacity-90" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-base font-bold text-white bg-white/[0.08]">
                              {(currentUser.name || '?')[0].toUpperCase()}
                            </div>
                          )}
                        </div>
                        <span
                          className={cn(
                            "absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-violet-600 text-white flex items-center justify-center transition-transform group-hover:scale-110",
                            isDark ? "border-2 border-[#0f0f14]" : "border-2 border-gray-50"
                          )}
                        >
                          <Plus className="h-3 w-3" strokeWidth={3} />
                        </span>
                      </>
                    )}
                  </div>
                </button>
                {myEntry ? (
                  <Link
                    to={`/traders/${currentUser.user_id || currentUser.id}`}
                    className={cn('text-[10px] font-medium max-w-[64px] truncate transition-colors', isDark ? 'text-gray-300 hover:text-violet-300' : 'text-gray-700 hover:text-violet-600')}
                    data-testid="sparks-self-name-link"
                  >
                    Your Spark
                  </Link>
                ) : (
                  <span className={cn('text-[10px] font-medium max-w-[64px] truncate', isDark ? 'text-gray-300' : 'text-gray-700')}>
                    Add
                  </span>
                )}
              </div>
            )}

            {/* Other authors */}
            {authors
              .filter(a => !currentUser || a.user_id !== (currentUser.user_id || currentUser.id))
              .map((a) => {
                const realIdx = authors.indexOf(a);
                return (
                  <div key={a.user_id} className="flex flex-col items-center gap-1.5 shrink-0 group">
                    <button
                      type="button"
                      onClick={() => openAuthor(realIdx)}
                      data-testid={`sparks-author-${a.user_id}`}
                      aria-label={`View Sparks from ${a.author_name}`}
                      className="rounded-full outline-none focus-visible:ring-2 focus-visible:ring-violet-500/40 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
                    >
                      <SparksRing hasActive viewed={!a.has_unviewed} size={60} thickness={2}>
                        {a.author_picture ? (
                          <img src={a.author_picture} alt={a.author_name || ''} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-base font-bold text-white bg-white/[0.08]">
                            {(a.author_name || 'T')[0].toUpperCase()}
                          </div>
                        )}
                      </SparksRing>
                    </button>
                    <Link
                      to={`/traders/${a.user_id}`}
                      data-testid={`sparks-author-name-link-${a.user_id}`}
                      className={cn(
                        'text-[10px] font-medium max-w-[64px] truncate transition-colors',
                        isDark ? 'text-gray-300 hover:text-violet-300' : 'text-gray-700 hover:text-violet-600'
                      )}
                      title={a.author_name || 'Trader'}
                    >
                      {a.author_name || 'Trader'}
                    </Link>
                  </div>
                );
              })}

            {/* Empty-tray inline CTA */}
            {authors.length === 0 && showSelfButton && (
              <div
                className={cn(
                  'flex items-center gap-2 self-center ml-1 px-3 py-1.5 rounded-full border text-[11px]',
                  isDark
                    ? 'border-white/10 bg-white/[0.02] text-gray-400'
                    : 'border-gray-200 bg-white text-gray-500'
                )}
                data-testid="sparks-empty-hint"
              >
                <Sparkles className="h-3 w-3 text-violet-400" />
                Be the first to share a Vibe today
              </div>
            )}

            {/* Anonymous empty state — invite to sign in */}
            {authors.length === 0 && !showSelfButton && (
              <Link
                to="/login"
                className={cn(
                  'flex items-center gap-2 self-center ml-1 px-3 py-1.5 rounded-full border text-[11px] transition-colors',
                  isDark
                    ? 'border-white/10 bg-white/[0.02] text-gray-400 hover:bg-white/[0.05] hover:text-violet-300'
                    : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-violet-600'
                )}
                data-testid="sparks-signin-hint"
              >
                <Sparkles className="h-3 w-3 text-violet-400" />
                Sign in to share a Vibe
              </Link>
            )}
          </div>

          {/* Right fade + chevron */}
          {canScrollRight && (
            <>
              <div
                aria-hidden
                className={cn(
                  "pointer-events-none absolute inset-y-0 right-0 w-10 z-10",
                  isDark
                    ? "bg-gradient-to-l from-[#0a0a0f] to-transparent"
                    : "bg-gradient-to-l from-gray-50 to-transparent"
                )}
              />
              <button
                type="button"
                aria-label="Scroll right"
                onClick={() => scrollBy(320)}
                className={cn(
                  "absolute right-0 top-1/2 -translate-y-1/2 z-20 h-7 w-7 rounded-full flex items-center justify-center border transition-all",
                  isDark
                    ? "bg-[#12121a] border-white/10 text-gray-300 hover:bg-[#1a1a24] hover:text-white"
                    : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                  "shadow-[0_4px_10px_-4px_rgba(0,0,0,0.4)]"
                )}
                data-testid="sparks-scroll-right"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Viewer modal */}
      {openIndex >= 0 && authors[openIndex] && (
        <SparksViewer
          authors={authors}
          startIndex={openIndex}
          currentUser={currentUser}
          onClose={closeViewer}
          onDeleted={(spId) => {
            setAuthors(prev => prev.map(a => ({ ...a, sparks: a.sparks.filter(s => s.spark_id !== spId) })).filter(a => a.sparks.length > 0));
          }}
        />
      )}

      {/* Create dialog */}
      {createOpen && (
        <CreateSparkDialog onClose={() => setCreateOpen(false)} onCreated={handleCreated} />
      )}
    </div>
  );
}
